import { useState } from 'react';
import { Plus, Trash2, ImagePlus, X } from 'lucide-react';
import {
    Button,
    Input,
    Label,
    Card,
    Select,
    Checkbox,
} from '@dukene/shared';

const COMMON_COLORS = [
    { name: 'Black', hex: '#000000' },
    { name: 'White', hex: '#FFFFFF' },
    { name: 'Red', hex: '#EF4444' },
    { name: 'Blue', hex: '#3B82F6' },
    { name: 'Green', hex: '#22C55E' },
    { name: 'Yellow', hex: '#EAB308' },
    { name: 'Purple', hex: '#A855F7' },
    { name: 'Pink', hex: '#EC4899' },
    { name: 'Orange', hex: '#F97316' },
    { name: 'Gray', hex: '#6B7280' },
    { name: 'Navy', hex: '#1E3A8A' },
];

const parseColorValue = (value: string) => {
    if (value.includes('|')) {
        const [name, hex] = value.split('|');
        return { name, hex };
    }
    return { name: value, hex: null };
};

const formatAttributeValue = (value: string) => {
    return parseColorValue(value).name;
};

export interface Attribute {
    name: string;
    type: 'SELECT' | 'SWATCH' | 'BUTTON';
    values: string[];
}

export interface Variant {
    title: string;
    sku: string;
    price: number;
    compareAtPrice?: number;
    quantity: number;
    available: boolean;
    image?: string; // Variant-specific image URL or preview
    attributeValues: Record<string, string>;
}

interface VariantManagerProps {
    hasVariants: boolean;
    onHasVariantsChange: (hasVariants: boolean) => void;
    attributes: Attribute[];
    onAttributesChange: (attributes: Attribute[]) => void;
    variants: Variant[];
    onVariantsChange: (variants: Variant[]) => void;
    defaultPrice?: number;
}

export default function VariantManager({
    hasVariants,
    onHasVariantsChange,
    attributes,
    onAttributesChange,
    variants,
    onVariantsChange,
    defaultPrice = 0,
}: VariantManagerProps) {
    const [showAttributeForm, setShowAttributeForm] = useState(false);
    const [newAttribute, setNewAttribute] = useState<{ name: string; type: Attribute['type'] }>({
        name: '',
        type: 'SELECT',
    });
    const [newAttributeValue, setNewAttributeValue] = useState('');
    const [newAttributeHex, setNewAttributeHex] = useState('#000000');

    // Generate variants from attribute combinations
    const generateVariants = (attrs: Attribute[]) => {
        if (attrs.length === 0) {
            onVariantsChange([]);
            return;
        }

        const combinations: Record<string, string>[] = [];

        const generate = (index: number, current: Record<string, string>) => {
            if (index === attrs.length) {
                combinations.push({ ...current });
                return;
            }

            const attr = attrs[index];
            for (const value of attr.values) {
                current[attr.name] = value;
                generate(index + 1, current);
            }
        };

        generate(0, {});

        const newVariants: Variant[] = combinations.map((combo) => {
            const title = Object.entries(combo)
                .map(([_, value]) => formatAttributeValue(value))
                .join(' / ');
            const existingVariant = variants.find(
                (v) => JSON.stringify(v.attributeValues) === JSON.stringify(combo)
            );

            return (
                existingVariant || {
                    title,
                    sku: '',
                    price: defaultPrice,
                    quantity: 0,
                    available: true,
                    attributeValues: combo,
                }
            );
        });

        onVariantsChange(newVariants);
    };

    // Add new attribute
    const addAttribute = () => {
        if (!newAttribute.name.trim()) return;

        const attr: Attribute = {
            name: newAttribute.name,
            type: newAttribute.type,
            values: [],
        };

        const newAttrs = [...attributes, attr];
        onAttributesChange(newAttrs);
        setNewAttribute({ name: '', type: 'SELECT' });
        setShowAttributeForm(false);
    };

    // Remove attribute
    const removeAttribute = (index: number) => {
        const newAttrs = attributes.filter((_, i) => i !== index);
        onAttributesChange(newAttrs);
        generateVariants(newAttrs);
    };

    // Add value to attribute
    const addAttributeValue = (attrIndex: number, forcedValue?: string) => {
        let valueToAdd = '';

        if (forcedValue) {
            valueToAdd = forcedValue;
        } else {
            if (attributes[attrIndex].type === 'SWATCH') {
                if (!newAttributeValue.trim()) return; // Name is required for swatches
                valueToAdd = `${newAttributeValue.trim()}|${newAttributeHex}`;
            } else {
                if (!newAttributeValue.trim()) return;
                valueToAdd = newAttributeValue.trim();
            }
        }

        const newAttrs = [...attributes];
        if (!newAttrs[attrIndex].values.includes(valueToAdd)) {
            newAttrs[attrIndex].values.push(valueToAdd);
            onAttributesChange(newAttrs);
            setNewAttributeValue('');
            if (newAttrs[attrIndex].type === 'SWATCH') {
                setNewAttributeHex('#000000');
            }
            generateVariants(newAttrs);
        }
    };

    // Remove value from attribute
    const removeAttributeValue = (attrIndex: number, valueIndex: number) => {
        const newAttrs = [...attributes];
        newAttrs[attrIndex].values.splice(valueIndex, 1);
        onAttributesChange(newAttrs);
        generateVariants(newAttrs);
    };

    // Update variant field
    const updateVariant = (index: number, field: keyof Variant, value: any) => {
        const newVariants = [...variants];
        newVariants[index] = { ...newVariants[index], [field]: value };
        onVariantsChange(newVariants);
    };

    // Handle variant image upload
    const handleVariantImageChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateVariant(index, 'image', reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Variants</h2>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="hasVariants"
                        checked={hasVariants}
                        onChange={(e) => onHasVariantsChange(e.target.checked)}
                    />
                    <Label htmlFor="hasVariants" className="cursor-pointer">
                        This product has options, like size or color
                    </Label>
                </div>
            </div>

            {hasVariants && (
                <div className="space-y-6">
                    {/* Attributes Section */}
                    <div className="space-y-4 pt-4 border-t">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium">Product Options</h3>
                            {!showAttributeForm && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowAttributeForm(true)}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Option
                                </Button>
                            )}
                        </div>

                        {/* Add Attribute Form */}
                        {showAttributeForm && (
                            <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                                            Option Name
                                        </Label>
                                        <Input
                                            type="text"
                                            value={newAttribute.name}
                                            onChange={(e) =>
                                                setNewAttribute({ ...newAttribute, name: e.target.value })
                                            }
                                            placeholder="e.g., Size, Color"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                                            Display Type
                                        </Label>
                                        <Select
                                            value={newAttribute.type}
                                            onChange={(e) =>
                                                setNewAttribute({
                                                    ...newAttribute,
                                                    type: e.target.value as Attribute['type'],
                                                })
                                            }
                                        >
                                            <option value="SELECT">Dropdown</option>
                                            <option value="BUTTON">Buttons</option>
                                            <option value="SWATCH">Color Swatches</option>
                                        </Select>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        onClick={addAttribute}
                                    >
                                        Add
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setShowAttributeForm(false);
                                            setNewAttribute({ name: '', type: 'SELECT' });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Existing Attributes */}
                        {attributes.map((attr, attrIndex) => (
                            <div key={attrIndex} className="p-4 bg-muted/30 rounded-lg space-y-4 border border-border/50">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-semibold">{attr.name}</h4>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                                            {attr.type === 'SELECT'
                                                ? 'Dropdown'
                                                : attr.type === 'BUTTON'
                                                    ? 'Buttons'
                                                    : 'Color Swatches'}
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeAttribute(attrIndex)}
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>

                                {/* Attribute Values */}
                                <div className="space-y-3">
                                    <div className="flex flex-wrap gap-2">
                                        {attr.values.map((value, valueIndex) => {
                                            const { name, hex } = parseColorValue(value);
                                            return (
                                                <span
                                                    key={valueIndex}
                                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-secondary text-secondary-foreground border border-border/50 shadow-sm gap-2"
                                                >
                                                    {hex && (
                                                        <span
                                                            className="w-3 h-3 rounded-full border border-black/10"
                                                            style={{ backgroundColor: hex }}
                                                        />
                                                    )}
                                                    {name}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAttributeValue(attrIndex, valueIndex)}
                                                        className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>

                                    {/* Quick Color Selection for Swatches */}
                                    {attr.type === 'SWATCH' && (
                                        <div className="flex flex-wrap gap-2 py-2">
                                            {COMMON_COLORS.map((color) => (
                                                <button
                                                    key={color.name}
                                                    type="button"
                                                    onClick={() => addAttributeValue(attrIndex, `${color.name}|${color.hex}`)}
                                                    className="group relative flex flex-col items-center gap-1"
                                                >
                                                    <div
                                                        className="w-8 h-8 rounded-full border border-border shadow-sm group-hover:scale-110 transition-transform"
                                                        style={{ backgroundColor: color.hex }}
                                                    />
                                                    <span className="text-[10px] text-muted-foreground">{color.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add Value Input */}
                                    <div className="flex flex-col gap-3 p-3 bg-muted/20 rounded-md">
                                        <div className="flex gap-2 items-center">
                                            {attr.type === 'SWATCH' && (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={newAttributeHex}
                                                        onChange={(e) => setNewAttributeHex(e.target.value)}
                                                        className="w-10 h-10 p-0 border-0 bg-transparent cursor-pointer rounded overflow-hidden"
                                                    />
                                                </div>
                                            )}
                                            <Input
                                                type="text"
                                                value={newAttributeValue}
                                                onChange={(e) => setNewAttributeValue(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addAttributeValue(attrIndex);
                                                    }
                                                }}
                                                placeholder={attr.type === 'SWATCH' ? "Color name (e.g. Forest Green)" : `Add ${attr.name.toLowerCase()} value...`}
                                                className="h-9"
                                            />
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => addAttributeValue(attrIndex)}
                                                className="whitespace-nowrap"
                                            >
                                                Add Value
                                            </Button>
                                        </div>
                                        {attr.type === 'SWATCH' && (
                                            <p className="text-[11px] text-muted-foreground italic">
                                                Select a color and give it a name to add a new swatch.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Variants Table */}
                    {variants.length > 0 && (
                        <div className="mt-8 space-y-4">
                            <h3 className="text-sm font-medium">
                                Variant Combinations ({variants.length})
                            </h3>
                            <div className="overflow-x-auto border border-border rounded-xl">
                                <table className="min-w-[900px] w-full divide-y divide-border">
                                    <thead className="bg-muted/50">
                                        <tr>
                                            <th className="w-20 px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Image
                                            </th>
                                            <th className="min-w-[150px] px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Variant
                                            </th>
                                            <th className="w-32 px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                selling Price
                                            </th>
                                            <th className="w-32 px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                discounted price
                                            </th>
                                            <th className="w-40 px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                SKU
                                            </th>
                                            <th className="w-28 px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Stock
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-card divide-y divide-border">
                                        {variants.map((variant, index) => (
                                            <tr key={index} className="hover:bg-muted/20 transition-colors">
                                                {/* Variant Image */}
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="relative w-12 h-12">
                                                        {variant.image ? (
                                                            <>
                                                                <img
                                                                    src={variant.image}
                                                                    alt={variant.title}
                                                                    className="w-full h-full object-cover rounded-md border border-border shadow-sm"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateVariant(index, 'image', undefined)}
                                                                    className="absolute -top-2 -right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 shadow-sm"
                                                                >
                                                                    <X className="w-2.5 h-2.5" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <label className="w-full h-full border-2 border-dashed border-border rounded-md flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                                                                <ImagePlus className="w-5 h-5 text-muted-foreground" />
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={(e) => handleVariantImageChange(index, e)}
                                                                    className="hidden"
                                                                />
                                                            </label>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Variant Title */}
                                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                    {variant.title}
                                                </td>

                                                {/* Price */}
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <Input
                                                        type="number"
                                                        value={variant.price}
                                                        onChange={(e) =>
                                                            updateVariant(index, 'price', parseFloat(e.target.value) || 0)
                                                        }
                                                        min="0"
                                                        step="0.01"
                                                        className="w-28 h-10 text-base"
                                                    />
                                                </td>

                                                {/* Compare At Price */}
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <Input
                                                        type="number"
                                                        value={variant.compareAtPrice || ''}
                                                        onChange={(e) =>
                                                            updateVariant(
                                                                index,
                                                                'compareAtPrice',
                                                                e.target.value ? parseFloat(e.target.value) : undefined
                                                            )
                                                        }
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                        className="w-28 h-10 text-base"
                                                    />
                                                </td>

                                                {/* SKU */}
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <Input
                                                        type="text"
                                                        value={variant.sku || ''}
                                                        onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                                                        placeholder="SKU"
                                                        className="w-36 h-10 text-base"
                                                    />
                                                </td>

                                                {/* Quantity */}
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <Input
                                                        type="number"
                                                        value={variant.quantity || 0}
                                                        onChange={(e) =>
                                                            updateVariant(index, 'quantity', parseInt(e.target.value) || 0)
                                                        }
                                                        min="0"
                                                        className="w-24 h-10 text-base"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}
